import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useAttendanceToday,
  useAttendanceHistory,
  useClockIn,
  useClockOut,
} from '@saiens/shared/hooks';
import { formatWorkHours } from '@saiens/shared/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ArrowLeft, AlertCircle } from 'lucide-react';

export function AttendancePage() {
  const navigate = useNavigate();
  const { data: todayData, isLoading } = useAttendanceToday();
  const { data: historyItems } = useAttendanceHistory(7);
  const clockIn = useClockIn();
  const clockOut = useClockOut();

  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const isCheckedIn = todayData?.attendance_state === 'checked_in';
  const isPending = clockIn.isPending || clockOut.isPending;

  const handleClock = useCallback(() => {
    if (isPending) return;
    if (isCheckedIn) {
      clockOut.mutate();
    } else {
      clockIn.mutate();
    }
  }, [isCheckedIn, isPending, clockIn, clockOut]);

  // Find today's first check_in and last check_out from records
  const todayFirstIn = todayData?.today_records?.[0]?.check_in;
  const todayLastOut = todayData?.today_records
    ?.filter((r) => r.check_out)
    .at(-1)?.check_out;

  const formatRecordTime = (datetime: string | null): string => {
    if (!datetime) return '--:--';
    // datetime format: "2026-02-08 08:52:00"
    const parts = datetime.split(' ');
    if (parts.length >= 2) {
      return parts[1].substring(0, 5);
    }
    return '--:--';
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center gap-3 bg-white px-4 py-3 shadow-sm">
        <button onClick={() => navigate(-1)} className="rounded-full p-1 hover:bg-gray-100">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-semibold">打卡系統</h1>
      </div>

      <div className="px-4 py-6">
        {/* Date & Clock */}
        <div className="mb-6 text-center">
          <p className="text-sm text-gray-500">
            {todayData?.today_date || currentTime.toLocaleDateString('zh-TW')}
            {' '}
            ({todayData?.day_of_week || ['日', '一', '二', '三', '四', '五', '六'][currentTime.getDay()]})
          </p>
          <p className="mt-1 font-mono text-4xl font-bold text-gray-900">
            {currentTime.toLocaleTimeString('zh-TW', { hour12: false })}
          </p>
        </div>

        {/* Clock Button */}
        <div className="mb-8 flex justify-center">
          <button
            onClick={handleClock}
            disabled={isPending}
            className={`flex h-36 w-36 items-center justify-center rounded-full shadow-lg transition-all active:scale-95 ${
              isCheckedIn
                ? 'bg-orange-500 hover:bg-orange-600'
                : 'bg-teal-500 hover:bg-teal-600'
            } ${isPending ? 'opacity-60' : ''}`}
          >
            {isPending ? (
              <LoadingSpinner size="lg" className="border-white border-t-transparent" />
            ) : (
              <div className="text-center text-white">
                <p className="text-2xl font-bold">
                  {isCheckedIn ? '下班打卡' : '上班打卡'}
                </p>
              </div>
            )}
          </button>
        </div>

        {/* Today Status */}
        <div className="mb-6 rounded-xl bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold text-gray-500">今日出勤</h2>
          <div className="flex justify-around">
            <div className="text-center">
              <p className="text-xs text-gray-400">上班</p>
              <p className="mt-1 font-mono text-lg font-semibold text-gray-900">
                {formatRecordTime(todayFirstIn ?? null)}
              </p>
            </div>
            <div className="h-10 w-px bg-gray-200" />
            <div className="text-center">
              <p className="text-xs text-gray-400">下班</p>
              <p className="mt-1 font-mono text-lg font-semibold text-gray-900">
                {formatRecordTime(todayLastOut ?? null)}
              </p>
            </div>
          </div>
        </div>

        {/* History */}
        <div className="rounded-xl bg-white p-4 shadow-sm">
          <h2 className="mb-3 text-sm font-semibold text-gray-500">近期出勤紀錄</h2>
          {historyItems && historyItems.length > 0 ? (
            <div className="space-y-2">
              {historyItems.map((item) => (
                <div
                  key={item.date}
                  className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm ${
                    item.is_anomaly ? 'bg-red-50' : 'bg-gray-50'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className="w-20 font-mono text-gray-600">
                      {item.date.substring(5)}
                    </span>
                    <span className="text-xs text-gray-400">({item.day_of_week})</span>
                    {item.is_anomaly && (
                      <AlertCircle className="h-3.5 w-3.5 text-red-500" />
                    )}
                  </div>
                  <div className="flex items-center gap-3 font-mono text-gray-700">
                    <span>{item.check_in || '--:--'}</span>
                    <span className="text-gray-300">~</span>
                    <span>{item.check_out || '--:--'}</span>
                    <span className="w-14 text-right text-xs text-gray-500">
                      {item.work_hours != null ? formatWorkHours(item.work_hours) : '-'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="py-4 text-center text-sm text-gray-400">暫無紀錄</p>
          )}
        </div>
      </div>
    </div>
  );
}
